import React, { createContext, forwardRef, useCallback, useMemo, useState } from 'react'
import { Form as BSForm } from 'react-bootstrap'

import { useUrlFrom } from '../../hooks/useUrlFrom'

import { FormContextType, DataType } from '../../types'

export const FormContext = createContext<FormContextType | undefined>( undefined )

type FormProps = {
    formName: string
    setInvalid?: ( value: boolean ) => void
    onFinishCallback: ( form: FormContextType, submitter: HTMLInputElement ) => Promise<void> | void
    className?: string
    initialData: Partial<DataType>
    children: React.ReactNode
}

export const Form = forwardRef<HTMLFormElement, FormProps>( ( { formName, setInvalid, onFinishCallback, className, initialData, children }: FormProps, ref ) => {
    
    const urlFrom = useUrlFrom()

    const [ processing, setProcessing ] = useState( false )
    const [ validated, setValidated ] = useState( false )
    const [ isInvalid, setIsInvalid ] = useState<boolean|undefined>( undefined )
    const [ data, setData ] = useState<DataType>( initialData as DataType )

    const setDataCallback = useCallback( ( value: DataType ) => {
        setData({ ...data, ...value })
    }, [ data, setData ] )

    const contextValue: FormContextType = useMemo( () => ({ processing, validated, urlFrom, data, setData: setDataCallback }), [ processing, validated, isInvalid, urlFrom, data, setDataCallback ] )

    const onFinish = useCallback( async ( ev: React.FormEvent<HTMLFormElement> ) => {
        ev.stopPropagation()
        setProcessing( true )
        ev.preventDefault()
        const form = ev.currentTarget
        const submitter = (ev.nativeEvent as SubmitEvent).submitter as HTMLInputElement
        if( ! form.checkValidity() ) {
            setIsInvalid( true )
            if( setInvalid ) setInvalid( true )
            setValidated( true )
        } else {
            setIsInvalid( false )
            if( setInvalid ) setInvalid( false )
            setValidated( true )
            await onFinishCallback( { ...contextValue, processing: true, validated: true }, submitter )
        }
        setProcessing( false )
    }, [ contextValue, onFinishCallback ] )

    return(<FormContext.Provider value={ contextValue }>
        <BSForm 
            ref={ ref }
            noValidate
            validated={ validated }
            name={ formName }
            className={ [ `${ formName.replace( '_', '-' ) }-form`, 'mx-auto', className || '' ]. join( ' ' ) }
            onSubmit={ ( ev ) => void onFinish( ev ) }
        >
            { children }
        </BSForm>
    </FormContext.Provider>)
} )
